import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { CustomButton } from '../../../components/buttons/customButton';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import { useUpdateOnboardingStep } from '../../../apiHooks/mutations/onboarding/useUpdateOnboardingStep';
import { useUpdateOrganizationSettings } from '../../../apiHooks/mutations/organizationMutations/organizationSettingsMutations';
import { OrganizationSettingsInterface } from '../../../interfaces/organizationSettings.interface';
import { ORGANIZATION_SETTINGS } from '../../../constants/pageIdentifiers';
import GoogleTabIcon from '../../../assets/icons/tab/google-tab-icon.svg';
import metaIcon from '../../../assets/icons/tab/integrationsMetaIcon.png';
import BingTabIcon from '../../../assets/icons/tab/bing-tab-icon.svg';
import SnapchatIcon from '../../../assets/icons/tab/logo-snapchat.svg';
import LinkedInTabIcon from '../../../assets/icons/tab/linkedInTabIcon.svg';
import ShopifyIcon from '../../../assets/icons/sidebar/shopify.svg';
import WordPressIcon from '../../../assets/icons/tab/wordpress-tab-icon.svg';
import WooCommerceIcon from '../../../assets/icons/tab/woocommerce-tab-icon.svg';
import CustomIntegrationIcon from '../../../assets/icons/tab/custom-integration-icon.svg';

interface IntegrationStepProps {
  onContinue: () => void;
}

const AD_PROVIDERS: {
  name: string;
  slug: string;
  settingsKey: keyof OrganizationSettingsInterface;
  icon: string;
}[] = [
  { name: 'Google', slug: 'google', settingsKey: 'googleClientId', icon: GoogleTabIcon },
  { name: 'Meta', slug: 'facebook', settingsKey: 'facebookToken', icon: metaIcon },
  { name: 'Bing', slug: 'bing', settingsKey: 'microsoftRefreshToken', icon: BingTabIcon },
  { name: 'Snapchat', slug: 'snapchat', settingsKey: 'isSnapchatActive', icon: SnapchatIcon },
  { name: 'LinkedIn', slug: 'linked-in', settingsKey: 'linkedinToken', icon: LinkedInTabIcon },
];

const PLATFORMS: { key: string; icon: string }[] = [
  { key: 'shopify', icon: ShopifyIcon },
  { key: 'wordpress', icon: WordPressIcon },
  { key: 'woocommerce', icon: WooCommerceIcon },
  { key: 'custom', icon: CustomIntegrationIcon },
];
type PlatformType = typeof PLATFORMS[number]['key'];

const IntegrationStep: FC<IntegrationStepProps> = ({ onContinue }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const organizationSettings = useOrganizationSettingsStore((s) => s.organizationSettings);
  const { mutateAsync: updateStep, isLoading: isSaving } = useUpdateOnboardingStep();
  const { mutateAsync: updateSettings } = useUpdateOrganizationSettings();

  const isShopifyOrg = !!organizationSettings.shopifyEnabled;

  const [selectedPlatform, setSelectedPlatform] = useState<PlatformType | null>(
    () => (organizationSettings.integrationType as PlatformType) || (isShopifyOrg ? 'shopify' : null),
  );

  useEffect(() => {
    queryClient.invalidateQueries([ORGANIZATION_SETTINGS]);
  }, [queryClient]);

  useEffect(() => {
    if (isShopifyOrg) setSelectedPlatform('shopify');
  }, [isShopifyOrg]);

  const isConnected = (key: keyof OrganizationSettingsInterface) => !!organizationSettings[key];
  const hasAnyConnection = AD_PROVIDERS.some((p) => isConnected(p.settingsKey));
  const canContinue = hasAnyConnection && !!selectedPlatform;

  const handleContinue = async () => {
    if (selectedPlatform) {
      await updateSettings({ integrationType: selectedPlatform });
    }
    await updateStep({ step: 'integration', status: 'completed' });
    onContinue();
  };

  return (
    <div className="flex w-full max-w-3xl flex-col gap-6">
      {/* Ad Providers */}
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <h1 className="mb-1.5 text-center text-lg font-semibold text-gray-900">
          {t('setup.step.integration')}
        </h1>
        <p className="mb-8 text-center text-sm text-gray-500">
          {t('setup.integration.subtitle')}
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {AD_PROVIDERS.map((provider) => {
            const connected = isConnected(provider.settingsKey);

            return (
              <button
                key={provider.slug}
                type="button"
                onClick={() => {
                  if (!connected) navigate(`/integrations/ad-providers/${provider.slug}`);
                }}
                className={`
                  relative flex flex-col items-center gap-3 rounded-xl border-2 px-4 py-6 transition-all
                  ${connected
                    ? 'cursor-default border-green bg-green/5'
                    : 'cursor-pointer border-gray-200 bg-white hover:border-primary/40 hover:shadow-md'
                  }
                `}
              >
                {connected && (
                  <div className="absolute right-2.5 top-2.5 flex h-6 w-6 items-center justify-center rounded-full bg-green">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M2 7l3.5 3.5L12 4"
                        stroke="white"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}

                <img
                  src={provider.icon}
                  alt={provider.name}
                  className="h-10 w-10 object-contain"
                />

                <span className="text-sm font-semibold text-gray-800">{provider.name}</span>

                <span
                  className={`text-xs font-medium ${connected ? 'text-green' : 'text-gray-400'}`}
                >
                  {connected ? t('setup.integration.connected') : t('setup.integration.notConnected')}
                </span>
              </button>
            );
          })}
        </div>

        {!hasAnyConnection && (
          <p className="mt-4 text-center text-xs text-gray-400">
            {t('setup.integration.connectAtLeastOne')}
          </p>
        )}
      </div>

      {/* Conversion Platform */}
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <h2 className="mb-1.5 text-center text-lg font-semibold text-gray-900">
          {t('setup.integration.platformTitle')}
        </h2>
        <p className="mb-6 text-center text-sm text-gray-500">
          {isShopifyOrg
            ? t('setup.integration.shopifyLocked')
            : t('setup.integration.platformSubtitle')}
        </p>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {PLATFORMS.map((platform) => {
            const selected = selectedPlatform === platform.key;
            const locked = isShopifyOrg && platform.key !== 'shopify';

            return (
              <button
                key={platform.key}
                type="button"
                disabled={isShopifyOrg}
                onClick={() => { if (!isShopifyOrg) setSelectedPlatform(platform.key); }}
                className={`
                  relative flex flex-col items-center gap-3 rounded-xl border-2 px-3 py-5 transition-all
                  ${selected
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 bg-white'
                  }
                  ${locked
                    ? 'cursor-not-allowed opacity-40'
                    : isShopifyOrg
                      ? 'cursor-default'
                      : 'cursor-pointer hover:border-primary/40 hover:shadow-md'
                  }
                `}
              >
                {selected && (
                  <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M2 7l3.5 3.5L12 4"
                        stroke="white"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
                <img
                  src={platform.icon}
                  alt={platform.key}
                  className="h-8 w-8 object-contain"
                />
                <span className="text-sm font-semibold text-gray-800">
                  {t(`setup.integration.platform.${platform.key}`)}
                </span>
              </button>
            );
          })}
        </div>

        {!selectedPlatform && (
          <p className="mt-4 text-center text-xs text-gray-400">
            {t('setup.integration.selectPlatform')}
          </p>
        )}
      </div>

      {/* Continue */}
      <CustomButton
        className="w-full justify-center py-2.5"
        disabled={!canContinue || isSaving}
        onClick={handleContinue}
      >
        {isSaving ? t('setup.saving') : `${t('setup.continue')} \u2192`}
      </CustomButton>
    </div>
  );
};

export default IntegrationStep;
